import { formatKWHNumber } from '@/helpers/kwhFormatter';
import { percentageFormatter } from '@/helpers/percentageFormatter';
import { Box, Button, FormikTextInput } from '@palmetto/palmetto-components';
import { Field } from 'formik';
import { ChangeEvent } from 'react';

interface SystemComparisonSectionProps {
  hasCurrentContract?: boolean;
  contractedValue: number;
  newValue: number;
  percentDiff: number;
  fieldName: string;
  fieldId: string;
  suffix: string;
  isSubmitting: boolean;
  isLoading: boolean;
  contractedLabel: string;
  installedLabel: string;
  diffLabel: string;
  showChangeOrderButton: boolean;
  changeOrderButtonLabel: string;
  onChangeOrderClick: () => void;
  systemSizeChangeCalculator?: boolean;
  onFieldChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const SystemComparisonSection = ({
  hasCurrentContract,
  contractedValue,
  newValue,
  percentDiff,
  fieldName,
  fieldId,
  suffix,
  isSubmitting,
  isLoading,
  contractedLabel,
  installedLabel,
  diffLabel,
  showChangeOrderButton,
  changeOrderButtonLabel,
  onChangeOrderClick,
  systemSizeChangeCalculator,
  onFieldChange,
}: SystemComparisonSectionProps) => {
  const canDisplayDiff = contractedValue !== newValue;

  const formatValue = (v: number) => {
    if (suffix.toLowerCase() === 'kwh') {
      return formatKWHNumber(v);
    }
    return String(v);
  };

  if (!hasCurrentContract) {
    return (
      <Field
        name={fieldName}
        id={fieldId}
        component={FormikTextInput}
        isDisabled={isSubmitting || isLoading}
        suffix={suffix}
        maxWidth="2xl"
        onChange={onFieldChange}
      />
    );
  }

  return (
    <Box>
      <Box childGap="md" direction="column">
        <Box margin="0 0 0 0" childGap="xs">
          <Box fontWeight="medium">{contractedLabel}</Box>
          {!systemSizeChangeCalculator && (
            <Box fontSize="sm" color="body-secondary" fontWeight="regular">
              An active contract is present on this account.
            </Box>
          )}
          <Box fontSize="lg" color="body-primary">
            {formatValue(contractedValue)} {suffix}
          </Box>
        </Box>
        {canDisplayDiff && (
          <Box margin="0 0 0 0" childGap="xs">
            <Box fontWeight="medium">{installedLabel}</Box>
            <Box fontSize="lg" color="body-primary">
              {formatValue(newValue)} {suffix}
            </Box>
          </Box>
        )}
        {canDisplayDiff && (
          <Box margin="0 0 0 0" childGap="xs">
            <Box fontWeight="medium">{diffLabel}</Box>
            <Box fontSize="lg" color="body-primary">
              {percentageFormatter.format(percentDiff)}
            </Box>
          </Box>
        )}
        {showChangeOrderButton && (
          <Box margin="0 0 0 0" childGap="xs">
            <Button
              variant="primary"
              tone="neutral"
              size="sm"
              onClick={onChangeOrderClick}
              data-testid="void-contract-action-button"
              iconSuffix="arrow-right"
              className="danger"
            >
              {changeOrderButtonLabel}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
